import React from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import PageHeader from '../components/PageHeader';
import CompanyLogos from '../components/CompanyLogos';
import { services, team } from '../mock';
import {
  ArrowUpRight, Check, FileText, ClipboardCheck, Layers, Building2, Landmark,
  Calculator, ScrollText, Quote, PlayCircle,
} from 'lucide-react';
import { SectionHeader } from '../components/Services';

const modules = [
  { icon: FileText, title: 'Form 1040 — Individual Returns', desc: 'W-2, 1099, deductions, credits, and every major schedule attached to individual returns.' },
  { icon: Building2, title: 'Form 1120 & 1120-S — Corporations', desc: 'C-Corp, S-Corp taxable income, book-tax reconciliation, and shareholder distributions.' },
  { icon: Layers, title: 'Form 1065 — Partnerships', desc: 'Partnership returns, K-1 distributions, capital account maintenance, and 754 elections.' },
  { icon: Landmark, title: 'State Tax Compliance', desc: 'State-specific filings, apportionment factors, nexus analysis, and multi-state returns.' },
  { icon: Calculator, title: 'Tax Software Training', desc: 'Hands-on with QuickBooks, Zoho Books, Drake Tax, and UltraTax CS — the tools Big 14 use.' },
  { icon: ScrollText, title: 'IRS Notices & Representation', desc: 'Read a CP notice, draft a response, prep for an audit, and handle appeals — end to end.' },
];

const capstones = [
  { title: 'File 3 mock 1040 returns', desc: 'From a shoebox of W-2s, 1099-Bs and Schedule K-1s — prepare and e-file mock individual returns.' },
  { title: 'Prepare an S-Corp 1120-S', desc: 'Book to tax adjustment, M-1 reconciliation, distribute K-1s, and file the full return in Drake.' },
  { title: 'Respond to a CP2000 notice', desc: 'Draft a full-length response letter with supporting documents, ready for the client’s signature.' },
  { title: 'Multi-state apportionment', desc: 'Compute state apportionment for a manufacturer selling in CA, TX, and NY — file all three returns.' },
];

const tools = [
  { name: 'QuickBooks Online', tag: 'Bookkeeping' },
  { name: 'Zoho Books', tag: 'Bookkeeping' },
  { name: 'Drake Tax', tag: 'Tax software' },
  { name: 'UltraTax CS', tag: 'Tax software' },
  { name: 'Lacerte', tag: 'Tax software' },
  { name: 'ProConnect', tag: 'Tax software' },
  { name: 'IRS e-Services', tag: 'IRS portal' },
  { name: 'CCH Axcess', tag: 'Enterprise' },
];

const outcomes = [
  { n: '200+', l: 'hours on live software' },
  { n: '15', l: 'return types covered' },
  { n: '3–5', l: 'weeks to job-ready' },
  { n: '₹8–15 LPA', l: 'placement range' },
];

const testimonials = [
  {
    quote: 'By week 4 I was preparing real 1120-S returns supervised by a US EA. Nothing beats that kind of on-the-job learning.',
    name: 'Sneha Kulkarni',
    role: 'US Tax Analyst • Withum India',
    image: '/images/site/34-photo-1544005313-94ddf0286df2.jpg',
  },
  {
    quote: 'The Drake Tax lab sessions were unbelievable. I walked into my BDO interview and did a live return — got the offer next day.',
    name: 'Vikram Shah',
    role: 'Tax Associate • BDO India',
    image: '/images/site/35-photo-1519085360753-af0119f7cbe7.jpg',
  },
];

const faqs = [
  { q: 'Do I need to know Indian tax before joining?', a: 'No prior tax knowledge required. We start from what a W-2 looks like and build up to complex partnership returns.' },
  { q: 'Is this course only for EA aspirants?', a: 'No. Many CAs, MBAs and commerce grads take this course purely to work in US tax outsourcing without pursuing EA.' },
  { q: 'Which software licenses do I get?', a: 'Full 6-month student licenses for Drake Tax, Zoho Books, and QuickBooks Online. You keep your return files after the course ends.' },
  { q: 'Can I get placed after only this course (without EA)?', a: 'Yes. Big 14 firms hire US Tax Analysts and Preparers with strong software skills. Our alumni are placed in analyst roles at EY, KPMG and Withum.' },
];

const USTaxation = () => {
  return (
    <div className="font-body bg-paper text-[#1c1c1c]">
      <Navbar />
      <PageHeader
        eyebrow="Practical Training"
        title={<>Master <em className="italic">US Taxation</em> from scratch</>}
        description="Hands-on training with real US tax forms, from Form 1040 to complex corporate returns — taught by practicing EAs."
        breadcrumb="US Taxation"
      />

      {/* Modules */}
      <section className="py-16 md:py-20">
        <div className="max-w-6xl mx-auto px-6">
          <SectionHeader
            eyebrow="Curriculum"
            title={<>Six modules. One <em className="italic">complete practitioner</em>.</>}
            description="Every module is 4 weeks: 2 weeks theory, 2 weeks lab. Every lab ends with a graded mock return."
          />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
            {modules.map((m, i) => {
              const Icon = m.icon;
              return (
                <div key={i} className="card-soft rounded-2xl p-6 md:p-7">
                  <div className="flex items-start gap-4">
                    <div className="w-11 h-11 rounded-xl bg-[#143324] text-[#f5f1e6] flex items-center justify-center shrink-0">
                      <Icon size={18} strokeWidth={1.7} />
                    </div>
                    <div>
                      <div className="text-[11px] tracking-wider uppercase text-[#3d3d3d] font-body mb-1">Module {String(i + 1).padStart(2, '0')}</div>
                      <h3 className="font-body font-semibold text-[#143324] text-lg md:text-xl mb-2">{m.title}</h3>
                      <p className="text-[14px] text-[#3d3d3d] leading-relaxed font-body">{m.desc}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Tools */}
      <section className="py-16 md:py-20 bg-paper-soft relative">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <div>
              <h2 className="font-serif-display text-[#143324] text-4xl md:text-5xl leading-tight mb-5">
                Real <em className="italic">tools</em>. Real <em className="italic">clients</em>. Real <em className="italic">practice</em>.
              </h2>
              <p className="text-[#3d3d3d] font-body leading-relaxed mb-6">
                Beyond theory — spend 200 hours working through live and dummy tax returns on the same software used by Big 14 firms. Ship your first US tax return before you graduate.
              </p>
              <ul className="space-y-3 mb-8">
                {['QuickBooks Online & Desktop', 'Zoho Books & Zoho Payroll', 'Drake Tax & UltraTax CS', 'IRS e-file Provider portal'].map((t, i) => (
                  <li key={i} className="flex items-center gap-3 text-[14px] text-[#1c1c1c] font-body">
                    <Check size={16} className="text-[#143324]" strokeWidth={2.5} /> {t}
                  </li>
                ))}
              </ul>
              <Link to="/contact" className="inline-flex items-center gap-2 btn-primary px-6 py-3 rounded-full text-sm font-body">
                Book a Free Demo <ArrowUpRight size={15} />
              </Link>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <img src={services[1].images[0]} alt="software" className="aspect-[3/4] object-cover rounded-2xl" />
              <img src={services[1].images[1]} alt="forms" className="aspect-[3/4] object-cover rounded-2xl mt-8" />
            </div>
          </div>
        </div>
      </section>

      {/* Tool Stack Grid */}
      <section className="py-16 md:py-20">
        <div className="max-w-6xl mx-auto px-6">
          <SectionHeader
            eyebrow="Software Stack"
            title={<>Certified on every tool a <em className="italic">US CPA firm</em> uses</>}
            description="These aren’t screenshots. You get full working licenses for the entire course duration."
          />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {tools.map((t, i) => (
              <div key={i} className="card-soft rounded-2xl p-5 md:p-6 text-center">
                <div className="w-12 h-12 rounded-xl bg-[#f5f1e6] border border-[#143324]/10 flex items-center justify-center mx-auto mb-3">
                  <Calculator size={18} className="text-[#143324]" strokeWidth={1.7} />
                </div>
                <p className="font-body font-semibold text-[#143324] text-[14px] mb-1">{t.name}</p>
                <p className="text-[10.5px] tracking-wider uppercase text-[#3d3d3d] font-body">{t.tag}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Capstone Projects */}
      <section className="py-16 md:py-24 bg-paper-soft relative">
        <div className="squiggle-bg" />
        <div className="max-w-6xl mx-auto px-6 relative">
          <SectionHeader
            eyebrow="Capstone Projects"
            title={<>Four <em className="italic">real</em> deliverables you can show recruiters</>}
            description="A portfolio of tax work is worth more than a certificate. Every project is graded and signed off by an EA."
          />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
            {capstones.map((c, i) => (
              <div key={i} className="card-soft rounded-2xl p-6 md:p-7">
                <div className="flex items-baseline gap-3 mb-3">
                  <span className="font-serif-display text-[#143324] italic text-3xl">{String(i + 1).padStart(2, '0')}</span>
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-[#143324]/8 text-[10.5px] tracking-wider uppercase text-[#143324] font-body">
                    <ClipboardCheck size={11} /> Graded
                  </span>
                </div>
                <h3 className="font-body font-semibold text-[#143324] text-lg mb-2">{c.title}</h3>
                <p className="text-[13.5px] text-[#3d3d3d] leading-relaxed font-body">{c.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Big Numbers */}
      <section className="py-16 md:py-20">
        <div className="max-w-6xl mx-auto px-6">
          <div className="card-soft rounded-3xl p-8 md:p-14 bg-[#f5f1e6] text-[#143324]">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {outcomes.map((b, i) => (
                <div key={i}>
                  <div className="font-serif-display text-[#143324] text-4xl md:text-6xl leading-none mb-2">{b.n}</div>
                  <p className="text-[13px] text-[#3d3d3d] font-body">{b.l}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 md:py-24 bg-paper-soft">
        <div className="max-w-5xl mx-auto px-6">
          <SectionHeader
            eyebrow="Placed & Practicing"
            title={<>Alumni working the <em className="italic">2026 filing season</em></>}
            description="What our US tax alumni say six months after graduation."
          />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
            {testimonials.map((t, i) => (
              <div key={i} className="card-soft rounded-2xl p-6 md:p-8 flex flex-col">
                <Quote size={22} className="text-[#143324] mb-4" strokeWidth={1.7} />
                <p className="text-[15px] text-[#1c1c1c] leading-relaxed font-body mb-6 flex-1">{t.quote}</p>
                <div className="flex items-center gap-3 pt-4 border-t border-[#143324]/10">
                  <img src={t.image} alt={t.name} className="w-11 h-11 rounded-full object-cover" />
                  <div>
                    <p className="font-body font-semibold text-[#143324] text-[14px]">{t.name}</p>
                    <p className="text-[12px] text-[#3d3d3d] font-body">{t.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Instructor callout */}
      <section className="py-16 md:py-20">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center card-soft rounded-3xl p-8 md:p-12">
            <div className="flex items-center gap-5">
              <img src={team[0].image} alt={team[0].name} className="w-24 h-24 rounded-2xl object-cover" />
              <div>
                <p className="text-[11px] tracking-wider uppercase text-[#3d3d3d] font-body mb-1">Lead Instructor</p>
                <h4 className="font-serif-display text-[#143324] text-2xl md:text-3xl leading-tight">{team[0].name}</h4>
                <p className="text-[13px] text-[#3d3d3d] font-body">{team[0].role} • 12 years US tax practice</p>
              </div>
            </div>
            <p className="text-[#3d3d3d] text-[15px] leading-relaxed font-body">
              “Half the market teaches theory. We’ll teach you to actually finish a US return, e-file it, respond to notices,
              and communicate with the client — the exact skill set Big 14 recruiters are testing for.”
            </p>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 md:py-24 bg-paper-soft">
        <div className="max-w-4xl mx-auto px-6">
          <SectionHeader
            eyebrow="Frequently Asked"
            title={<>Questions before you <em className="italic">enroll</em></>}
            description="Short answers to the questions we hear from every prospective batch."
          />
          <div className="space-y-3">
            {faqs.map((f, i) => (
              <details key={i} className="card-soft rounded-2xl px-6 py-5 group" data-testid={`ust-faq-${i}`}>
                <summary className="cursor-pointer font-body font-semibold text-[#143324] text-[15px] flex items-center justify-between list-none">
                  <span>{f.q}</span>
                  <span className="ml-3 w-6 h-6 rounded-full bg-[#f5f1e6] border border-[#143324]/15 flex items-center justify-center text-[#143324] group-open:rotate-45 transition-transform">+</span>
                </summary>
                <p className="mt-3 text-[13.5px] text-[#3d3d3d] leading-relaxed font-body">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 md:py-24">
        <div className="max-w-5xl mx-auto px-6">
          <div className="card-soft rounded-3xl p-8 md:p-14 bg-[#f5f1e6] text-[#143324] relative overflow-hidden">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
              <div>
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#143324]/15 bg-white/60 text-[11px] tracking-[0.2em] uppercase text-[#143324] font-body mb-4">
                  <PlayCircle size={12} /> Batch starts every 15 days
                </div>
                <h3 className="font-serif-display text-[#143324] text-4xl md:text-5xl leading-tight mb-4">
                  Ship your first <em className="italic">US tax return</em> in 90 days.
                </h3>
                <p className="text-[#3d3d3d] font-body leading-relaxed mb-2 max-w-md">
                  Book a free demo, meet the lead instructor, and take a live sample class.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row md:flex-col gap-3">
                <Link to="/contact" className="btn-primary inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full text-[14px] font-body">
                  Enroll now <ArrowUpRight size={15} />
                </Link>
                <a href="https://wa.me/917010492161" target="_blank" rel="noreferrer" className="btn-outline inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full text-[14px] font-body">
                  WhatsApp us
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <CompanyLogos />
      <Footer />
    </div>
  );
};

export default USTaxation;
